/**
 * 플래너 생성 플로우 추적 훅
 * 
 * 플래너 생성 전체 플로우의 의도-결과를 추적
 * - planner_flow_start: 플래너 생성 시작
 * - planner_flow_success: 플래너 생성 성공
 * - planner_flow_fail: 플래너 생성 실패
 * 
 * @example
 * ```tsx
 * function PlannerPage() {
 *   const plannerFlow = usePlannerFlow();
 *   
 *   useEffect(() => {
 *     plannerFlow.start();
 *   }, []);
 *   
 *   const handleComplete = async () => {
 *     try {
 *       const result = await createPlanner(data);
 *       plannerFlow.success();
 *     } catch (error) {
 *       plannerFlow.fail('backend_5xx');
 *     }
 *   };
 * }
 * ```
 */

import { useIntent } from '../useIntent';
import { track } from '../../../../core/client';

export interface PlannerFlowContext {
  planner_id?: number;
}

export interface PlannerFlowActions {
  start: (options?: {
    parentIntentId?: string;
    parentIntentType?: string;
  }) => string;
  success: () => void;
  fail: (reason?: string) => void;
}

/**
 * 플래너 생성 플로우 추적 훅
 */
export function usePlannerFlow(): PlannerFlowActions {
  const intent = useIntent<PlannerFlowContext>({
    type: 'planner_flow',
    requiredGate: 'none',
    onStart: ({ intent_id, parent_intent_id, required_gate }) => {
      track('planner_flow_start', {
        intent_id,
        parent_intent_id,
        required_gate,
      });
    },
    onSuccess: ({ intent_id, context, latency_ms }) => {
      track('planner_flow_success', {
        intent_id,
        planner_id: context?.planner_id,
        latency_ms,
      });
    },
    onFail: ({ intent_id, fail_reason }) => {
      track('planner_flow_fail', {
        intent_id,
        fail_reason,
      });
    },
  });

  return {
    start: (options) => intent.start(undefined, options),
    success: () => intent.success(),
    fail: (reason) => intent.fail(reason),
  };
}

